import { useState } from 'react';
import { useSchemes, useSchemeEstimate } from '../network';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Card, CardContent } from '../../../components/ui/Card';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { format } from 'date-fns';
import Decimal from 'decimal.js';
import { DEFAULT_CURRENCY } from '../../../lib/locale';

const DECAY_OPTIONS = [
  { value: 'LINEAR', label: 'Linear' },
  { value: 'SIGMOID', label: 'Sigmoid' },
  { value: 'UNIFORM', label: 'Uniform' },
];

function formatPaise(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: DEFAULT_CURRENCY,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  }
  // allow-silent-catch: pure display formatting fallback; on parse failure we just show the raw date string, nothing to surface to the user or log
  catch {
    return dateStr;
  }
}

export function EstimateCalculator() {
  const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(null);
  const [decayModel, setDecayModel] = useState<'LINEAR' | 'SIGMOID' | 'UNIFORM'>('LINEAR');
  const [startDiscount, setStartDiscount] = useState(30);
  const [endDiscount, setEndDiscount] = useState(5);
  const [showResults, setShowResults] = useState(false);

  const { data: schemesData, isLoading: schemesLoading } = useSchemes({ limit: 100 });
  const { data: estimate, isLoading: estimateLoading } = useSchemeEstimate(
    selectedSchemeId ?? '',
    { decayModel, assumedStartDiscountPct: startDiscount, assumedEndDiscountPct: endDiscount },
    showResults && !!selectedSchemeId,
  );

  const schemes = schemesData?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="max-w-[640px] space-y-4">
        <FormField label="Select Scheme" required>
          <select
            value={selectedSchemeId ?? ''}
            onChange={(e) => {
              setSelectedSchemeId(e.target.value || null);
              setShowResults(false);
            }}
            className="w-full h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={schemesLoading}
          >
            <option value="">Choose a scheme...</option>
            {schemes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} — {formatPaise(s.chitValuePaise)}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      <Card className="rounded-[14px]">
        <CardContent className="p-6">
          <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider mb-4">
            Assumptions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormSelect
              label="Decay Model"
              name="decayModel"
              value={decayModel}
              onChange={(e) => setDecayModel(e.target.value as typeof decayModel)}
              options={DECAY_OPTIONS}
            />
            <FormField label="Start Discount %" hint="Between 5 and 40">
              <Input
                type="number"
                min="5"
                max="40"
                value={startDiscount}
                onChange={(e) => setStartDiscount(new Decimal(e.target.value).toNumber())}
              />
            </FormField>
            <FormField label="End Discount %" hint="Between 5 and 40">
              <Input
                type="number"
                min="5"
                max="40"
                value={endDiscount}
                onChange={(e) => setEndDiscount(new Decimal(e.target.value).toNumber())}
              />
            </FormField>
          </div>
          <div className="mt-6">
            <Button
              variant="primary"
              loading={estimateLoading}
              disabled={!selectedSchemeId}
              onClick={() => setShowResults(true)}
            >
              Run Estimate
            </Button>
          </div>
        </CardContent>
      </Card>

      {showResults && estimateLoading && (
        <p className="text-sm text-text-secondary">Loading estimate...</p>
      )}

      {showResults && estimate && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-[14px]">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">Actual Dividends Paid</p>
                <p className="text-xl font-semibold text-text-primary font-mono mt-1">{formatPaise(estimate.totalActualDividendsPaid)}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[14px]">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">Projected Dividends</p>
                <p className="text-xl font-semibold text-text-primary font-mono mt-1">{formatPaise(estimate.totalProjectedDividends)}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[14px]">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">Projected Commissions</p>
                <p className="text-xl font-semibold text-text-primary font-mono mt-1">{formatPaise(estimate.totalProjectedCommissions)}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[14px]">
              <CardContent className="p-5">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">IRR Range</p>
                <p className="text-xl font-semibold text-text-primary font-mono mt-1">
                  {estimate.yieldProfiles.length > 0
                    ? (() => {
                        const yields = estimate.yieldProfiles.map((y) => y.annualizedYieldPct);
                        const min = Math.min(...yields);
                        const max = Math.max(...yields);
                        return `${min.toFixed(1)}% – ${max.toFixed(1)}%`;
                      })()
                    : '—'}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-[14px] overflow-hidden">
            <CardContent className="p-0">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Cycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Scheduled Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Subscription Due</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Prize Payout</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Dividend / Share</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Type</th>
                  </tr>
                </thead>
                <tbody className="bg-surface-1 divide-y divide-border">
                  {estimate.cycles.map((cycle) => (
                    <tr key={cycle.cycleNumber} className="hover:bg-surface-2">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{cycle.cycleNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{formatDate(cycle.scheduledDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(cycle.subscriptionDue)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(cycle.netPrizePayout)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(cycle.perSubscriberDividend)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {cycle.status === 'ACTUAL' ? (
                          <StatusBadge status="success" label="Confirmed" />
                        ) : (
                          <StatusBadge status="info" label="Projected" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{cycle.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {estimate.yieldProfiles.length > 0 && (
            <Card className="rounded-[14px] overflow-hidden">
              <CardContent className="p-0">
                <div className="px-6 py-3 bg-surface-2 border-b border-border">
                  <h3 className="text-sm font-semibold text-text-primary">Yield Profiles</h3>
                </div>
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-surface-2">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Win Cycle</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Total Paid In</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Net Prize</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Net Benefit</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Annualized Yield</th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-1 divide-y divide-border">
                    {estimate.yieldProfiles.map((profile) => (
                      <tr key={profile.winCycle} className="hover:bg-surface-2">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{profile.winCycle}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(profile.totalPaidIn)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(profile.netPrizeReceived)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{formatPaise(profile.netBenefit)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary text-right font-mono">{profile.annualizedYieldPct.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
